<template>
  <div class="welcome-container">
    <div class="animated-bg"></div>
    <div class="welcome-content">
<!-- Hero Section Updates -->
<div class="hero-section glass-card">
  <div class="hero-badge">
    <v-icon small>mdi-rocket-launch</v-icon>
    <span>Generate Videos From Conversations</span>
  </div>
  <h1 class="hero-title">
    <span class="gradient-text">Turn Questions Into</span>
    <br />
    <span class="highlight-text">Expert Conversations</span>
    <br />
    <span class="sub-highlight">Ship Videos in Minutes, Not Weeks</span>
  </h1>
  <p class="hero-subtitle">
    From a simple question to a complete multi-perspective video in minutes.
    Just ask a question, let AI research and generate the conversation, then export professional results.
  </p>

  <div class="hero-cta">
    <v-btn
      color="primary"
      x-large
      rounded
      elevation="0"
      @click="startFirstProject"
      class="primary-cta glass-button"
    >
      <v-icon left>mdi-play-circle</v-icon>
      Start Creating Now
    </v-btn>
  </div>

  <!-- Social Proof -->
  <div class="social-proof">
    <p>Trusted by teams at</p>
    <div class="company-logos">
      <span class="company-badge">A16Z Portfolio Companies</span>
    </div>
  </div>
</div>

<!-- New Citation & Expert Avatars Section -->
<div class="features-section glass-card">
  <h2 class="section-title gradient-text">Key Features</h2>
  <div class="features-grid">
    <!-- Inline Citations Feature -->
    <div class="feature-card">
      <div class="feature-icon">
        <v-icon color="primary" size="48">mdi-text-box-check</v-icon>
      </div>
      <h3 class="feature-title">Inline Citations</h3>
      <p class="feature-desc">
        Every fact is backed by sources from the web or your uploaded documents, with inline citations that can be fact-checked at your fingertips.
      </p>
      <div class="feature-example">
        <div class="citation-example">
          <p>"According to recent research, green energy investments have <span class="citation-highlight">increased by 47% over the past year [Source: Energy Report 2024]</span>."</p>
        </div>
      </div>
    </div>

    <!-- AI Expert Avatars Feature -->
    <div class="feature-card">
      <div class="feature-icon">
        <v-icon color="success" size="48">mdi-account-group</v-icon>
      </div>
      <h3 class="feature-title">AI Expert Avatars</h3>
      <p class="feature-desc">
        Start with our stock AI expert avatars that deliver powerful, informed scripts. Customize speakers or replace with your own team.
      </p>
      <div class="feature-example">
        <div class="avatar-gallery">
          <div class="avatar-item">
            <div class="avatar-circle tech"></div>
            <span>Tech Analyst</span>
          </div>
          <div class="avatar-item">
            <div class="avatar-circle finance"></div>
            <span>Financial Expert</span>
          </div>
          <div class="avatar-item">
            <div class="avatar-circle health"></div>
            <span>Health Specialist</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Multi-Perspective Conversations Feature -->
    <div class="feature-card">
      <div class="feature-icon">
        <v-icon color="error" size="48">mdi-chat-processing</v-icon>
      </div>
      <h3 class="feature-title">Multi-Perspective Conversations</h3>
      <p class="feature-desc">
        Generate balanced dialogues with different viewpoints, creating nuanced, engaging content that resonates with diverse audiences.
      </p>
      <div class="feature-example">
        <div class="perspective-example">
          <div class="perspective-line pro">
            <span class="perspective-label">For:</span>
            <span class="perspective-text">Key benefits include...</span>
          </div>
          <div class="perspective-line con">
            <span class="perspective-label">Against:</span>
            <span class="perspective-text">Potential challenges are...</span>
          </div>
          <div class="perspective-line neutral">
            <span class="perspective-label">Balanced:</span>
            <span class="perspective-text">Research suggests that...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- How We're Different Section Update -->
<div class="comparison-section glass-card">
  <h2 class="section-title gradient-text">How We're Different</h2>
  <div class="comparison-grid">
    <div class="comparison-column">
      <div class="comparison-header">
        <v-icon color="error" size="40">mdi-presentation-play</v-icon>
        <h3>Traditional Video Tools</h3>
      </div>
      <ul class="comparison-list">
        <li>Require you to write scripts manually</li>
        <li>Complex timelines and editing tools</li>
        <li>No research capabilities</li>
        <li>Generic content without citations</li>
        <li>Single perspective presentations</li>
        <li>High learning curve</li>
      </ul>
    </div>
    <div class="comparison-column highlight-column">
      <div class="comparison-header">
        <v-icon color="success" size="40">mdi-account-group</v-icon>
        <h3>UnStruct.ai</h3>
      </div>
      <ul class="comparison-list">
        <li>Generates complete multi-perspective conversations</li>
        <li>Simple conversation-based interface</li>
        <li>Researches topics using the web and your documents</li>
        <li>Cites sources for fact-checking</li>
        <li>Creates nuanced discussions with multiple viewpoints</li>
        <li>Zero learning curve - just ask a question</li>
      </ul>
    </div>
  </div>
</div>

<!-- Value Props Section Update -->
<div class="stats-section">
  <h2 class="section-title gradient-text">Core Benefits</h2>
  <div class="stats-grid">
    <div class="stat-card glass-card" style="--delay: 0.1s">
      <div class="stat-icon">
        <v-icon color="primary" size="40">mdi-clock-fast</v-icon>
      </div>
      <div class="stat-content">
        <h4 class="stat-value">Speed</h4>
        <p class="stat-desc">Create in minutes what traditionally takes weeks</p>
      </div>
      <div class="stat-bg"></div>
    </div>
    <div class="stat-card glass-card" style="--delay: 0.2s">
      <div class="stat-icon">
        <v-icon color="success" size="40">mdi-check-decagram</v-icon>
      </div>
      <div class="stat-content">
        <h4 class="stat-value">Accuracy</h4>
        <p class="stat-desc">Backed by research and inline citations</p>
      </div>
      <div class="stat-bg"></div>
    </div>
    <div class="stat-card glass-card" style="--delay: 0.3s">
      <div class="stat-icon">
        <v-icon color="secondary" size="40">mdi-account-group</v-icon>
      </div>
      <div class="stat-content">
        <h4 class="stat-value">Multiple Perspectives</h4>
        <p class="stat-desc">Balanced viewpoints that engage any audience</p>
      </div>
      <div class="stat-bg"></div>
    </div>
    <div class="stat-card glass-card" style="--delay: 0.4s">
      <div class="stat-icon">
        <v-icon color="error" size="40">mdi-refresh</v-icon>
      </div>
      <div class="stat-content">
        <h4 class="stat-value">Instant Iterations</h4>
        <p class="stat-desc">Change anything, anytime, in seconds</p>
      </div>
      <div class="stat-bg"></div>
    </div>
  </div>
</div>

<!-- Workflow Preview Update -->
<div class="workflow-section">
  <h2 class="section-title gradient-text">Your New Video Workflow</h2>
  <div class="workflow-timeline">
    <div class="workflow-item">
      <div class="workflow-icon">
        <v-icon>mdi-comment-text</v-icon>
      </div>
      <div class="workflow-content">
        <h4>Just Ask a Question</h4>
        <p>"Is blockchain worth investing in?"</p>
      </div>
    </div>
    <div class="workflow-arrow">→</div>
    <div class="workflow-item">
      <div class="workflow-icon">
        <v-icon>mdi-magnify</v-icon>
      </div>
      <div class="workflow-content">
        <h4>AI Researches & Creates</h4>
        <p>Finds sources and generates expert dialogue</p>
      </div>
    </div>
    <div class="workflow-arrow">→</div>
    <div class="workflow-item">
      <div class="workflow-icon">
        <v-icon>mdi-tune</v-icon>
      </div>
      <div class="workflow-content">
        <h4>Customize as Needed</h4>
        <p>Edit content or add your own materials</p>
      </div>
    </div>
    <div class="workflow-arrow">→</div>
    <div class="workflow-item">
      <div class="workflow-icon">
        <v-icon>mdi-download</v-icon>
      </div>
      <div class="workflow-content">
        <h4>1-Click Export & Ship</h4>
        <p>HD quality, ready for any platform</p>
      </div>
    </div>
  </div>
</div>

<!-- Testimonial Section Update -->
<div class="testimonial-section glass-card">
  <h3 class="sub-section-title gradient-text">What Teams Are Saying</h3>

  <!-- Testimonial Carousel -->
  <div class="testimonial-carousel">
    <!-- Testimonial 1 -->
    <div class="testimonial-card">
      <div class="testimonial-quote">
        <v-icon color="primary" size="32">mdi-format-quote-open</v-icon>
        <p>One thing we do really like is how fast we can make changes overall, some things are much faster than the traditional way of doing this type of video. And the dialogue the AI helped us create is more interesting than the one we started with.</p>
        <v-icon color="primary" size="32">mdi-format-quote-close</v-icon>
      </div>
      <div class="testimonial-author">
        <div class="author-image">
          <v-icon size="48">mdi-account-circle</v-icon>
        </div>
        <div class="author-info">
          <h4>Co-Founder</h4>
          <p>A16Z Portfolio Company</p>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Bring Your Dead Content to Life Section Update -->
<div class="archive-section glass-card">
  <h3 class="sub-section-title gradient-text">Bring Your Dead/Archive Content to Life</h3>
  <p class="archive-desc">
    Transform your existing documents, presentations, and knowledge base into engaging,
    multi-perspective conversations that captivate your audience.
  </p>
  <div class="archive-examples">
    <div class="archive-example">
      <div class="archive-before">
        <v-icon size="40" color="error">mdi-file-document</v-icon>
        <h4>Static Document</h4>
        <div class="document-preview">
          <div class="document-line"></div>
          <div class="document-line"></div>
          <div class="document-line"></div>
        </div>
      </div>
      <div class="archive-arrow">
        <v-icon>mdi-arrow-right</v-icon>
      </div>
      <div class="archive-after">
        <v-icon size="40" color="success">mdi-account-group</v-icon>
        <h4>Dynamic Conversation</h4>
        <div class="conversation-small-preview">
          <div class="small-speaker-line"></div>
          <div class="small-speaker-line"></div>
          <div class="small-speaker-line"></div>
        </div>
      </div>
    </div>
  </div>
</div>

      <!-- Real Use Case Section -->
      <div class="use-case-showcase glass-card">
        <h3 class="sub-section-title gradient-text">From Script to Screen in Record Time</h3>
        <div class="showcase-grid">
          <div class="showcase-item">
            <div class="showcase-icon">
              <v-icon size="48" color="primary">mdi-presentation</v-icon>
            </div>
            <h4>Investor Presentations</h4>
            <p>Turn your pitch deck into a dynamic video that gets meetings</p>
            <div class="showcase-stat">
              <span class="stat-number">2 hours</span>
              <span class="stat-label">vs 2 weeks traditional</span>
            </div>
          </div>
          <div class="showcase-item">
            <div class="showcase-icon">
              <v-icon size="48" color="secondary">mdi-youtube</v-icon>
            </div>
            <h4>Content Marketing</h4>
            <p>Create YouTube-ready explainers without a studio</p>
            <div class="showcase-stat">
              <span class="stat-number">$149</span>
              <span class="stat-label">vs $5,000+ per video</span>
            </div>
          </div>
          <div class="showcase-item">
            <div class="showcase-icon">
              <v-icon size="48" color="success">mdi-rocket</v-icon>
            </div>
            <h4>Product Launches</h4>
            <p>Ship announcement videos the same day you decide</p>
            <div class="showcase-stat">
              <span class="stat-number">Same day</span>
              <span class="stat-label">vs 3-week agency timeline</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Use Cases Grid -->
      <div class="use-cases">
        <h2 class="section-title gradient-text">Perfect For Every Use Case</h2>
        <div class="use-cases-grid">
          <div
            v-for="(useCase, index) in useCases"
            :key="index"
            class="use-case-card glass-card"
            :style="{'--delay': (index % 3) * 0.1 + 's'}"
          >
            <div class="use-case-icon">
              <v-icon :color="useCase.color" size="36">{{ useCase.icon }}</v-icon>
            </div>
            <h4>{{ useCase.title }}</h4>
            <p>{{ useCase.description }}</p>
            <div class="use-case-hover"></div>
          </div>
        </div>
      </div>
      <!-- Bottom CTA -->
      <div class="bottom-cta glass-card">
        <h2 class="cta-title">Start Creating Intelligent Video Conversations Today</h2>
        <p class="cta-subtitle">No credit card. No downloads. Just authentic, intelligent content.</p>
        <v-btn
          color="primary"
          x-large
          rounded
          @click="startFirstProject"
          class="final-cta"
        >
          Create Your First Video Now
        </v-btn>
        <div class="trust-indicators">
          <div class="trust-item">
            <v-icon small>mdi-shield-check</v-icon>
            <span>Enterprise Security</span>
          </div>
          <div class="trust-item">
            <v-icon small>mdi-lock</v-icon>
            <span>Your Data Stays Private</span>
          </div>
          <div class="trust-item">
            <v-icon small>mdi-heart</v-icon>
            <span>Loved by Teams</span>
          </div>
        </div>
      </div>

      <!-- Quick Actions Float -->
      <div class="quick-actions">
        <h3 class="quick-title">Quick Start</h3>
        <div class="action-buttons">
          <v-btn
            v-for="(action, index) in quickActions"
            :key="index"
            fab
            large
            :color="action.color"
            @click="action.method"
            class="action-fab"
            :style="{'--delay': index * 0.1 + 's'}"
          >
            <v-icon>{{ action.icon }}</v-icon>
          </v-btn>
        </div>
        <div class="action-labels">
          <span v-for="(action, index) in quickActions" :key="index">{{ action.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
        name: 'ModernWelcomePage',
        data() {
          return {
            steps: [
              {
                title: "Ask a Question",
                description: "Start with a simple question, upload documents, or import YouTube videos.",
                icon: "mdi-cloud-upload",
                color: "primary"
              },
              {
                title: "Generate Your Conversation",
                description: "AI researches your topic and creates engaging multi-perspective dialogues.",
                icon: "mdi-message-text",
                color: "secondary"
              },
              {
                title: "Customize Your Video",
                description: "Add visuals, edit content, adjust animations, and preview in real-time.",
                icon: "mdi-tune-variant",
                color: "success"
              },
              {
                title: "Export & Share",
                description: "Export your finished video or present live to your audience.",
                icon: "mdi-share-variant",
                color: "error"
              }
            ],
            useCases: [
              { title: "Investor Pitches", description: "Professional pitch decks that impress", icon: "mdi-presentation", color: "primary" },
              { title: "Product Demos", description: "Showcase features with engaging videos", icon: "mdi-rocket-launch", color: "secondary" },
              { title: "Training Materials", description: "Create educational content at scale", icon: "mdi-school", color: "success" },
              { title: "Marketing Content", description: "Produce videos 10x faster", icon: "mdi-bullhorn", color: "error" },
              { title: "Sales Presentations", description: "Close deals with compelling videos", icon: "mdi-handshake", color: "warning" },
              { title: "Internal Communications", description: "Engage teams with dynamic updates", icon: "mdi-account-group", color: "info" },
              { title: "Client Reports", description: "Transform data into visual stories", icon: "mdi-file-chart", color: "primary" },
              { title: "Course Content", description: "Build engaging learning experiences", icon: "mdi-video-box", color: "secondary" },
              { title: "Success Stories", description: "Turn testimonials into narratives", icon: "mdi-trophy", color: "success" }
            ],
            quickActions: [
              { icon: "mdi-file-document", label: "Upload Docs", method: () => this.openFileUploadDialog(), color: "primary" },
              { icon: "mdi-file-upload", label: "Submit Text", method: () => this.openSubmitTextDialog(), color: "secondary" },
              { icon: "mdi-youtube", label: "YouTube", method: () => this.openYoutubeSubmitDialog(), color: "error" },
              { icon: "mdi-image", label: "Upload Images", method: () => this.openImageUploadDialog(), color: "success" }
            ]
          };
        },
        methods: {
            startFirstProject() {
                this.$emit('start-first-project');
            },
            openAskQuestionDialog() {
                this.$emit('open-ask-question');
            },
            openFileUploadDialog() {
                this.$emit('open-file-upload');
            },
            openSubmitTextDialog() {
                this.$emit('open-submit-text');
            },
            openYoutubeSubmitDialog() {
                this.$emit('open-youtube-submit');
            },
            openImageUploadDialog() {
                this.$emit('open-image-upload');
            },
            openDemo() {
                // Implement demo functionality
            }
        }
    };
</script>

<style scoped>
/* Base Container */
.welcome-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.theme--dark .welcome-container {
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
}

/* Animated Background */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.1;
  z-index: 0;
}

.animated-bg::before,
.animated-bg::after {
  content: '';
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 234, 255, 0.3) 0%, transparent 70%);
  animation: float 20s infinite ease-in-out;
}

.animated-bg::before {
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.animated-bg::after {
  bottom: -200px;
  right: -200px;
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-20px, -50px) rotate(120deg); }
  66% { transform: translate(20px, -20px) rotate(240deg); }
}

/* Glass Card Effect */
.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

.theme--dark .glass-card {
  background: rgba(30, 41, 59, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* Welcome Content */
.welcome-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 60px 40px;
  margin-bottom: 60px;
  position: relative;
  overflow: hidden;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  animation: slideDown 0.6s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-title {
  font-size: clamp(36px, 6vw, 72px);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 30px;
  animation: fadeInUp 0.8s ease-out;
}

.gradient-text {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.highlight-text {
  color: #1f2937;
  display: inline-block;
  font-weight: 800;
}

.theme--dark .highlight-text {
  color: #f9fafb;
}

.sub-highlight {
  color: #00eaff;
  font-size: 0.9em;
  font-weight: 700;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-subtitle {
  font-size: 20px;
  color: #4b5563;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.6;
  animation: fadeIn 1s ease-out 0.3s both;
}

.theme--dark .hero-subtitle {
  color: #d1d5db;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Comparison Section (NEW) */
.comparison-section {
  padding: 60px 40px;
  margin-bottom: 60px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 40px;
}

.comparison-column {
  padding: 30px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
}

.theme--dark .comparison-column {
  background: rgba(30, 41, 59, 0.5);
}

.highlight-column {
  background: rgba(0, 234, 255, 0.05);
  border: 1px solid rgba(0, 234, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 234, 255, 0.1);
}

.theme--dark .highlight-column {
  background: rgba(0, 234, 255, 0.1);
}

.comparison-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.comparison-header h3 {
  font-size: 24px;
  font-weight: 700;
  margin-top: 16px;
}

.comparison-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.comparison-list li {
  padding: 12px 0;
  position: relative;
  padding-left: 28px;
  font-size: 16px;
  color: #4b5563;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.theme--dark .comparison-list li {
  color: #d1d5db;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.comparison-list li:last-child {
  border-bottom: none;
}

.comparison-list li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #00eaff;
  font-size: 24px;
  line-height: 1;
}

/* Research Section (NEW) */
.research-section {
  margin: 60px 0;
  padding: 60px;
}

.research-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
}

.research-subtitle {
  font-size: 24px;
  color: #00eaff;
  margin-bottom: 20px;
  font-weight: 600;
}

.research-desc {
  font-size: 18px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 24px;
}

.theme--dark .research-desc {
  color: #d1d5db;
}

.research-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.research-feature {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 500;
}

.search-preview {
  background: #f9fafb;
  border-radius: 16px;
  padding: 24px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.theme--dark .search-preview {
  background: #1f2937;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
}

.search-query {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.theme--dark .search-query {
  background: #374151;
  color: #f9fafb;
}

.search-result {
  padding: 16px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
}

.theme--dark .search-result {
  background: rgba(30, 41, 59, 0.5);
}

.result-source {
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
}

.theme--dark .result-source {
  color: #f9fafb;
}

.result-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
}

.theme--dark .result-item {
  color: #d1d5db;
}

/* Human-Centered Section (NEW) */
.human-centered-section {
  padding: 60px;
  margin: 60px 0;
  text-align: center;
}

.human-centered-desc {
  font-size: 18px;
  color: #4b5563;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

.theme--dark .human-centered-desc {
  color: #d1d5db;
}

.human-centered-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.human-centered-item {
  padding: 24px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.3s ease;
}

.human-centered-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.theme--dark .human-centered-item {
  background: rgba(30, 41, 59, 0.5);
}

.human-centered-item h4 {
  font-size: 20px;
  font-weight: 600;
  margin: 16px 0 12px;
}

.human-centered-item p {
  color: #6b7280;
  font-size: 15px;
  margin: 0;
}

.theme--dark .human-centered-item p {
  color: #d1d5db;
}

/* Enhanced Chat Preview */
.conversation-preview {
  margin-top: 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 16px;
}

.theme--dark .conversation-preview {
  background: rgba(255, 255, 255, 0.05);
}

.speaker-line {
  margin-bottom: 12px;
  font-size: 14px;
}

.speaker-name {
  font-weight: 600;
  color: #00eaff;
  margin-right: 8px;
}

.speaker-text {
  color: #4b5563;
}

.theme--dark .speaker-text {
  color: #d1d5db;
}

/* Archive Section (NEW) */
.archive-section {
  padding: 60px;
  margin: 60px 0;
  text-align: center;
}

.archive-desc {
  font-size: 18px;
  color: #4b5563;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

.theme--dark .archive-desc {
  color: #d1d5db;
}

.archive-examples {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.archive-example {
  display: flex;
  align-items: center;
  gap: 30px;
  max-width: 700px;
}

.archive-before,
.archive-after {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
}

.theme--dark .archive-before,
.theme--dark .archive-after {
  background: rgba(30, 41, 59, 0.5);
}

.archive-before h4,
.archive-after h4 {
  font-size: 18px;
  font-weight: 600;
  margin-top: 8px;
}

.document-preview {
  width: 100%;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.theme--dark .document-preview {
  background: #374151;
}

.document-line {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 12px;
}

.theme--dark .document-line {
  background: #4b5563;
}

.document-line:last-child {
  width: 60%;
}

.conversation-small-preview {
  width: 100%;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.theme--dark .conversation-small-preview {
  background: #374151;
}

.small-speaker-line {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.small-speaker-line::before {
  content: '';
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00eaff;
  margin-right: 12px;
  opacity: 0.5;
}

.small-speaker-line::after {
  content: '';
  display: inline-block;
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
}

.theme--dark .small-speaker-line::after {
  background: #4b5563;
}

/* Testimonial Section (NEW) */
.testimonial-section {
  padding: 60px;
  margin: 60px 0;
  text-align: center;
}

.testimonial-card {
  max-width: 800px;
  margin: 40px auto 0;
  padding: 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
}

.theme--dark .testimonial-card {
  background: rgba(30, 41, 59, 0.5);
}

.testimonial-quote {
  position: relative;
  margin-bottom: 30px;
}

.testimonial-quote p {
  font-size: 20px;
  line-height: 1.6;
  color: #4b5563;
  margin: 16px 0;
}

.theme--dark .testimonial-quote p {
  color: #d1d5db;
}

.testimonial-author {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.author-image {
  color: #8B5CF6;
}

.author-info h4 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
}

.author-info p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.theme--dark .author-info p {
  color: #9ca3af;
}

/* Hero CTA */
.hero-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  animation: fadeIn 1s ease-out 0.5s both;
}

.primary-cta {
  padding: 16px 40px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease !important;
}

.glass-button {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

.glass-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 234, 255, 0.3) !important;
}

.glass-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.glass-button:hover::before {
  left: 100%;
}

.cta-divider {
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
}

.secondary-cta {
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Social Proof */
.social-proof {
  margin-top: 40px;
  text-align: center;
  animation: fadeIn 1s ease-out 0.8s both;
}

.social-proof p {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 12px;
}

.company-logos {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.company-badge {
  display: inline-block;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.theme--dark .company-badge {
  background: rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
}

/* Stats Section */
.stats-section {
  margin-bottom: 100px;
}

.section-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  padding: 0 20px;
}

.stat-card {
  position: relative;
  padding: 30px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.8s ease-out var(--delay) both;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  margin-bottom: 20px;
  animation: pulse 4s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
}

.theme--dark .stat-value {
  color: #f9fafb;
}

.stat-desc {
  color: #6b7280;
  font-size: 15px;
  margin: 0;
}

.theme--dark .stat-desc {
  color: #d1d5db;
}

.stat-bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 234, 255, 0.05) 0%, transparent 70%);
  transform: rotate(45deg);
  transition: all 0.5s ease;
  opacity: 0;
}

.stat-card:hover .stat-bg {
  opacity: 1;
  transform: rotate(45deg) scale(1.2);
}

/* Use Case Showcase */
.use-case-showcase {
  margin: 100px 0;
  padding: 60px;
  text-align: center;
}

.sub-section-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 40px;
}

.showcase-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 40px;
}

.showcase-item {
  text-align: center;
}

.showcase-icon {
  margin-bottom: 20px;
}

.showcase-item h4 {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
}

.theme--dark .showcase-item h4 {
  color: #f9fafb;
}

.showcase-item p {
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.6;
}

.theme--dark .showcase-item p {
  color: #d1d5db;
}

.showcase-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #00eaff;
}

.stat-label {
  font-size: 14px;
  color: #9ca3af;
}

/* Interface Section */
.interface-section {
  margin: 100px 0;
  padding: 60px;
  position: relative;
  overflow: hidden;
}

.interface-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.interface-subtitle {
  font-size: 24px;
  color: #00eaff;
  margin-bottom: 20px;
  font-weight: 600;
}

.interface-desc {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.6;
}

.theme--dark .interface-desc {
  color: #d1d5db;
}

/* Chat Preview */
.chat-preview {
  background: #f9fafb;
  border-radius: 16px;
  padding: 24px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.theme--dark .chat-preview {
  background: #1f2937;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
}

.chat-bubble {
  padding: 12px 20px;
  border-radius: 20px;
  margin-bottom: 16px;
  font-size: 15px;
  animation: slideIn 0.5s ease-out both;
}

.chat-bubble.user {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  color: white;
  margin-left: auto;
  max-width: 80%;
  border-bottom-right-radius: 6px;
  animation-delay: 0.5s;
}

.chat-bubble.ai {
  background: white;
  color: #1f2937;
  max-width: 80%;
  border-bottom-left-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  animation-delay: 1s;
}

.theme--dark .chat-bubble.ai {
  background: #374151;
  color: #f9fafb;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  animation: slideIn 0.5s ease-out 1.5s both;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #9ca3af;
  border-radius: 50%;
  animation: bounce 1s infinite;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* Use Cases Grid */
.use-cases {
  margin-bottom: 100px;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  padding: 0 20px;
}

.use-case-card {
  padding: 30px;
  text-align: center;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.8s ease-out var(--delay) both;
  overflow: hidden;
}

.use-case-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.use-case-icon {
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.use-case-card h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
  position: relative;
  z-index: 1;
}

.theme--dark .use-case-card h4 {
  color: #f9fafb;
}

.use-case-card p {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  position: relative;
  z-index: 1;
}

.theme--dark .use-case-card p {
  color: #d1d5db;
}

.use-case-hover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(0, 234, 255, 0.08) 0%, transparent 70%);
  transform: scale(0);
  transition: transform 0.5s ease;
  pointer-events: none;
}

.use-case-card:hover .use-case-hover {
  transform: scale(1.5);
}

/* Workflow Section */
.workflow-section {
  margin: 100px 0;
  text-align: center;
}

.workflow-timeline {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 60px;
  flex-wrap: wrap;
}

.workflow-item {
  flex: 1;
  min-width: 200px;
  max-width: 250px;
  text-align: center;
}

.workflow-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
}

.workflow-content h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1f2937;
}

.theme--dark .workflow-content h4 {
  color: #f9fafb;
}

.workflow-content p {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

.theme--dark .workflow-content p {
  color: #d1d5db;
}

.workflow-arrow {
  font-size: 36px;
  color: #00eaff;
  opacity: 0.5;
}

/* Bottom CTA */
.bottom-cta {
  text-align: center;
  padding: 80px 40px;
  margin: 100px 0 40px;
}

.cta-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1f2937;
}

.theme--dark .cta-title {
  color: #f9fafb;
}

.cta-subtitle {
  font-size: 20px;
  color: #6b7280;
  margin-bottom: 40px;
}

.theme--dark .cta-subtitle {
  color: #d1d5db;
}

.final-cta {
  padding: 18px 48px !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  margin-top: 30px;
  box-shadow: 0 8px 24px rgba(0, 234, 255, 0.3) !important;
  text-transform: none !important;
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%) !important;
  color: white !important;
}

.trust-indicators {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 40px;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
}

.theme--dark .trust-item {
  color: #d1d5db;
}

/* Quick Actions */
.quick-actions {
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 100;
  text-align: center;
}

.quick-title {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 12px;
  opacity: 0.8;
}

.theme--dark .quick-title {
  color: #d1d5db;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.action-fab {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  animation: fadeInUp 0.6s ease-out var(--delay) both, float 6s infinite ease-in-out var(--delay);
}

.action-fab:hover {
  transform: scale(1.1) translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.action-labels {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #9ca3af;
}

.action-labels span {
  flex: 1;
  white-space: nowrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .interface-content, .research-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .chat-preview, .search-preview {
    max-width: 500px;
    margin: 0 auto;
  }

  .workflow-timeline {
    gap: 10px;
  }

  .workflow-item {
    min-width: 180px;
  }

  .workflow-arrow {
    font-size: 28px;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .welcome-content {
    padding: 20px;
  }

  .hero-section, .comparison-section, .research-section, .human-centered-section,
  .interface-section, .archive-section, .testimonial-section {
    padding: 40px 20px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .hero-cta {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .workflow-timeline {
    flex-direction: column;
  }

  .workflow-arrow {
    transform: rotate(90deg);
  }

  .showcase-grid {
    grid-template-columns: 1fr;
  }

  .use-cases-grid {
    grid-template-columns: 1fr;
  }

  .archive-example {
    flex-direction: column;
  }

  .archive-arrow {
    transform: rotate(90deg);
    margin: 16px 0;
  }

  .bottom-cta {
    padding: 60px 30px;
  }

  .cta-title {
    font-size: 32px;
  }

  .trust-indicators {
    flex-direction: column;
    gap: 16px;
  }

  .quick-actions {
    bottom: 20px;
    right: 20px;
  }

  .action-buttons {
    gap: 8px;
  }

  .action-fab {
    width: 48px !important;
    height: 48px !important;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .section-title, .sub-section-title {
    font-size: 24px;
  }

  .glass-card {
    border-radius: 16px;
    padding: 20px;
  }

  .action-labels {
    display: none;
  }

  .company-logos {
    flex-direction: column;
    align-items: center;
  }

  .workflow-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }

  .testimonial-quote p {
    font-size: 16px;
  }
}

/* Print Styles */
@media print {
  .quick-actions {
    display: none;
  }

  .animated-bg {
    display: none;
  }

  .glass-card {
    background: white;
    box-shadow: none;
  }
}

/* Animations and Transitions */
.welcome-container * {
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
}

/* Smooth Scroll */
html {
  scroll-behavior: smooth;
}

/* Focus States */
.v-btn:focus::before {
  opacity: 0.1 !important;
}

.glass-button:focus {
  outline: 2px solid #00eaff;
  outline-offset: 2px;
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .glass-card {
    border: 2px solid currentColor;
  }

  .gradient-text {
    -webkit-text-fill-color: currentColor;
    color: currentColor;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .welcome-container * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 234, 255, 0.3);
  border-radius: 4px;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 234, 255, 0.5);
}

.theme--dark ::-webkit-scrollbar-thumb {
  background: rgba(0, 234, 255, 0.4);
}

.theme--dark ::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 234, 255, 0.6);
}

/* Performance Optimizations */
.welcome-container {
  will-change: scroll-position;
}

.animated-bg::before,
.animated-bg::after {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.glass-card {
  transform: translateZ(0);
  will-change: transform;
}

/* Bottom CTA - Responsive fixes */
.bottom-cta {
  text-align: center;
  padding: 80px 40px;
  margin: 100px 0 40px;
}

.cta-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1f2937;
  word-wrap: break-word; /* Prevent overflow */
  line-height: 1.2;
}

/* Gradient Text Animation */
.gradient-text {
  background: linear-gradient(135deg, #00eaff 0%, #8B5CF6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
  animation: gradientShift 8s ease infinite;
  background-size: 200% 200%;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Mobile Responsive Fixes */
@media (max-width: 768px) {
  .bottom-cta {
    padding: 60px 30px;
  }

  .cta-title {
    font-size: 28px; /* Smaller on mobile */
  }
}

@media (max-width: 480px) {
  .bottom-cta {
    padding: 40px 20px;
  }

  .cta-title {
    font-size: 24px; /* Even smaller on small mobile */
  }

  .cta-subtitle {
    font-size: 16px;
  }

  .final-cta {
    font-size: 16px !important;
    padding: 14px 32px !important;
  }
}

/* Add margin at bottom for chat input on mobile */
@media (max-width: 768px) {
  .welcome-container {
    padding-bottom: 80px; /* Space for chat input */
  }
}

/* Key Features Section */
.features-section {
  padding: 60px 40px;
  margin-bottom: 60px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.feature-card {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 30px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.theme--dark .feature-card {
  background: rgba(30, 41, 59, 0.5);
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  margin-bottom: 20px;
  text-align: center;
}

.feature-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
}

.theme--dark .feature-title {
  color: #f9fafb;
}

.feature-desc {
  color: #6b7280;
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.6;
}

.theme--dark .feature-desc {
  color: #d1d5db;
}

.feature-example {
  margin-top: auto;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  padding: 16px;
}

.theme--dark .feature-example {
  background: rgba(30, 41, 59, 0.7);
}

/* Citation Example Styles */
.citation-example {
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
}

.theme--dark .citation-example {
  color: #d1d5db;
}

.citation-highlight {
  background: rgba(0, 234, 255, 0.15);
  border-bottom: 1px dashed #00eaff;
  padding: 2px 4px;
  border-radius: 4px;
}

/* Avatar Gallery Styles */
.avatar-gallery {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.avatar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.theme--dark .avatar-circle {
  border-color: #374151;
}

.avatar-circle.tech {
  background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
}

.avatar-circle.finance {
  background: linear-gradient(135deg, #10B981 0%, #3B82F6 100%);
}

.avatar-circle.health {
  background: linear-gradient(135deg, #F59E0B 0%, #EF4444 100%);
}

.avatar-item span {
  font-size: 12px;
  color: #6b7280;
}

.theme--dark .avatar-item span {
  color: #d1d5db;
}

/* Multi-Perspective Example Styles */
.perspective-example {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.perspective-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  font-size: 13px;
}

.perspective-line.pro {
  background: rgba(16, 185, 129, 0.1);
}

.perspective-line.con {
  background: rgba(239, 68, 68, 0.1);
}

.perspective-line.neutral {
  background: rgba(59, 130, 246, 0.1);
}

.perspective-label {
  font-weight: 600;
  min-width: 60px;
}

.perspective-line.pro .perspective-label {
  color: #10B981;
}

.perspective-line.con .perspective-label {
  color: #EF4444;
}

.perspective-line.neutral .perspective-label {
  color: #3B82F6;
}

.perspective-text {
  color: #4b5563;
}

.theme--dark .perspective-text {
  color: #d1d5db;
}

/* Testimonial Carousel Styles */
.testimonial-carousel {
  position: relative;
  max-width: 800px;
  margin: 40px auto 0;
}

.carousel-nav {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(0, 234, 255, 0.3);
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background: #00eaff;
  transform: scale(1.2);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
  }

  .avatar-gallery {
    flex-direction: column;
    gap: 20px;
  }
}

</style>
